import { skipToken, useQuery } from '@tanstack/react-query';

import { ApiClient } from '@provider-portal/common/api';
import {
  EvaluationMonth,
  PerformanceTier,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';

import { MONTHLY_UPDATE_MOCK, USE_BOLT_STAR_MONTHLY_UPDATE_MOCK } from './mockData';
import { isTopPerformerEnabled } from './utils';
import { useTargetingParameters } from '@provider-portal/common/experiments/targeting';

export interface BoltStarMonthlyUpdate {
  month: EvaluationMonth;
  tier: PerformanceTier;
}

function lastEvaluatedMonth(): EvaluationMonth {
  const now = new Date();
  const year = now.getFullYear();
  // `getMonth()` is 0-based, API is 1-based
  const month = now.getMonth();
  return month === 0 ? { year: year - 1, month: 12 } : { year, month };
}

export function useBoltStarMonthlyUpdate(providerId: number | undefined) {
  const { apiClient } = useCommonAppServices();
  const isTopPerformerEnabledValue = isTopPerformerEnabled(useTargetingParameters());
  const month = lastEvaluatedMonth();

  const query = useQuery({
    queryKey: ['bolt-star-monthly-update', providerId, month.year, month.month],
    queryFn: USE_BOLT_STAR_MONTHLY_UPDATE_MOCK
      ? () => Promise.resolve(MONTHLY_UPDATE_MOCK)
      : providerId === undefined
      ? skipToken
      : () =>
          apiClient.deliveryProviderPortal.getTopPerformerMonthlyReport({
            provider_id: providerId,
            evaluation_month: month,
          }),
    enabled: isTopPerformerEnabledValue,
  });

  const update: BoltStarMonthlyUpdate | undefined =
    query.data?.new_report_available && query.data.tier !== undefined
      ? { month, tier: query.data.tier }
      : undefined;

  return { ...query, update, isTopPerformerEnabled: isTopPerformerEnabledValue };
}

interface AcknowledgeArgs {
  apiClient: ApiClient;
  providerId: number;
  month: EvaluationMonth;
}

export async function acknowledgeBoltStarMonthlyUpdate({
  apiClient,
  providerId,
  month,
}: AcknowledgeArgs): Promise<void> {
  if (USE_BOLT_STAR_MONTHLY_UPDATE_MOCK) {
    return;
  }

  await apiClient.deliveryProviderPortal.acknowledgeTopPerformerMonthlyReport({
    provider_id: providerId,
    evaluation_month: month,
  });
}
